import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { toTitleCaseWithExclusions } from "utilities/racingUtilities";

type HighlightProps = {
  text?: string;
  matchedWords: string[];
  html: string;
  className?: string;
  convertToTitleCase?: boolean;
};

export const Highlight = ({
  text,
  matchedWords,
  html,
  className = "",
  convertToTitleCase = false,
}: HighlightProps) => {
  if (matchedWords.length === 0 && text)
    return <>{convertToTitleCase ? toTitleCaseWithExclusions(text) : text}</>;

  const cleanHTML = useMemo(() => {
    // If we aren't converting it, we can just use the html provided
    if (!convertToTitleCase) {
      return DOMPurify.sanitize(html);
    }
    // We need to convert it to title case, so we extract the <marks>, then capitalise it and add them back in
    // 1. Find all html tags in the string
    const tagRegex = /<[^>]+>/g;
    const matches = [...html.matchAll(tagRegex)];

    // 2. Capitalise the string without the html tags
    const textFromHtml = html.replace(tagRegex, "");
    let titleCaseStrHtml = toTitleCaseWithExclusions(textFromHtml);

    // 3. Loop through the matches and add the tags into the capitalised string
    matches.forEach((match) => {
      const prefix = titleCaseStrHtml.slice(0, match.index);
      const tag = match[0];
      const postfix = titleCaseStrHtml.slice(match.index);

      titleCaseStrHtml = prefix + tag + postfix;
    });

    return DOMPurify.sanitize(titleCaseStrHtml);
  }, [html, convertToTitleCase]);

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: cleanHTML }}
    />
  );
};
